/**
 * A fixed-size ring buffer between the game loop (producer, once per frame) and the HUD's own
 * animation frame (consumer). Overflow drops the oldest entries; nothing allocates per push.
 */
export class RingChannel<T> {
  private readonly items: (T | undefined)[];
  private head = 0;
  private count = 0;
  dropped = 0;

  constructor(readonly capacity = 256) {
    this.items = new Array<T | undefined>(capacity);
  }

  get size(): number {
    return this.count;
  }

  push(item: T): void {
    const index = (this.head + this.count) % this.capacity;
    if (this.count === this.capacity) {
      this.head = (this.head + 1) % this.capacity;
      this.dropped++;
    } else {
      this.count++;
    }
    this.items[index] = item;
  }

  /** Removes and returns everything in arrival order. */
  drain(): T[] {
    const out: T[] = [];
    for (let i = 0; i < this.count; i++) {
      const item = this.items[(this.head + i) % this.capacity];
      if (item !== undefined) out.push(item);
      this.items[(this.head + i) % this.capacity] = undefined;
    }
    this.head = 0;
    this.count = 0;
    return out;
  }

  clear(): void {
    this.drain();
    this.dropped = 0;
  }
}
